/**
 * Ventana del alta de WhatsApp con el enlace de YCloud («Partner Direct Link», TP Lite; 11.2, G694, G695, ADR-0185). El
 * SDK de YCloud (`https://connect.ycloud.com/open/sdk/v1.js`) se carga **solo al pulsar** y abre `onboarding_url` en una
 * ventana emergente con `YCloudOnboarding.open`. Lo que dice la ventana solo cambia los textos del panel: el alta la completa
 * el servidor con el aviso de YCloud, y el panel lo sabe consultando `getChannelConnection`.
 *
 * El enlace es una credencial temporal: no se guarda en el navegador (ni `localStorage` ni la URL), no va a analítica ni a
 * la consola.
 *
 * Las pruebas cambian `altaYCloud.abrir` para simular la ventana sin salir a la red.
 */
export type SucesoYCloud =
    /** `onStatus`: `CONNECTED` es que la persona terminó (falta que YCloud avise al servidor). */
    | { tipo: 'estado'; conectado: boolean }
    /** `onError` `POPUP_BLOCKED`: el navegador no deja abrir la ventana; se ofrece el enlace en otra pestaña. */
    | { tipo: 'bloqueada' }
    /** `onError` `INVALID_ONBOARDING_URL`: el enlace no vale (caducado o ya usado); hay que empezar otra alta. */
    | { tipo: 'enlace-no-valido' }
    /** `onClose`: la persona cerró la ventana antes de terminar. No cancela el alta. */
    | { tipo: 'cerrada' }
    /** No se ha podido cargar el SDK de YCloud (sin conexión o bloqueado): como si se bloqueara la ventana. */
    | { tipo: 'sin-sdk' };

interface ResultadoYCloud {
    state?: string;
}
interface ErrorYCloud {
    code?: string;
}
interface SdkYCloud {
    open: (opciones: {
        onboardingUrl: string;
        onStatus?: (r: ResultadoYCloud) => void;
        onError?: (e: ErrorYCloud) => void;
        onClose?: (e: unknown) => void;
    }) => void;
}

export const SDK_YCLOUD = 'https://connect.ycloud.com/open/sdk/v1.js';

let cargando: Promise<SdkYCloud | null> | null = null;

function sdk(): Promise<SdkYCloud | null> {
    const ya = (window as unknown as { YCloudOnboarding?: SdkYCloud }).YCloudOnboarding;
    if (ya !== undefined) {
        return Promise.resolve(ya);
    }
    cargando ??= new Promise<SdkYCloud | null>((listo) => {
        const guion = document.createElement('script');
        guion.src = SDK_YCLOUD;
        guion.async = true;
        guion.onload = () => {
            listo((window as unknown as { YCloudOnboarding?: SdkYCloud }).YCloudOnboarding ?? null);
        };
        guion.onerror = () => {
            cargando = null;
            listo(null);
        };
        document.head.appendChild(guion);
    });
    return cargando;
}

async function abrirConSdk(enlace: string, avisar: (s: SucesoYCloud) => void): Promise<void> {
    const yc = await sdk();
    if (yc === null) {
        avisar({ tipo: 'sin-sdk' });
        return;
    }
    yc.open({
        onboardingUrl: enlace,
        onStatus: (r) => {
            avisar({ tipo: 'estado', conectado: r.state === 'CONNECTED' });
        },
        onError: (e) => {
            avisar(e.code === 'POPUP_BLOCKED' ? { tipo: 'bloqueada' } : { tipo: 'enlace-no-valido' });
        },
        onClose: () => {
            avisar({ tipo: 'cerrada' });
        },
    });
}

export const altaYCloud = {
    /** Carga el SDK (la primera vez) y abre la ventana de YCloud con el enlace; lo que pase llega por `avisar`. */
    abrir: (enlace: string, avisar: (s: SucesoYCloud) => void): Promise<void> => abrirConSdk(enlace, avisar),
};
